import { useCallback, useContext, useMemo, useState } from "react";
import { StorageContext } from "./storage-context";
import {
  ICustomerCompany,
  IModality,
  IProduct,
  ResponseUnloadData,
  ScheduleList,
  StorageEndpoint,
  StorageFilters,
} from "../../modules/storage/models/storage-dto";
import { StorageContextProps } from "./models/storage-dto";
import { PipelineScheduleStatus } from "../../modules/operational/models/GetPipelineSchedulesDTO";
import { routesConfig } from "../../config/routes";
import useDialogAlert from "../../hooks/DialogAlert";
import { useIocContext } from "../ioc/IocContext";
import { useDistributor } from "../distributor/DistributorContext";
import { IStorageService } from "../../modules/storage/models/IStorageService";
import { Types } from "../../ioc/types";
import AppError from "../../utils/appError";

export const ROWS_PER_PAGE = 10;

export const initialFilters: StorageFilters = {
  productId: "",
  subsidiaryId: "",
  scheduleDateFrom: "",
  scheduleDateTo: "",
  searchOV: "",
  page: 1,
  limit: ROWS_PER_PAGE,
};

export const StorageProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [list, setList] = useState<ResponseUnloadData>({ rows: [], count: 0 });
  const [tab, setTab] = useState(0);
  const [statusVendor, setStatusVendor] = useState<PipelineScheduleStatus>(
    PipelineScheduleStatus.VENDOR_PENDING
  );
  const [loadingData, setLoadingData] = useState<boolean>(false);
  const [filters, setFilters] = useState<StorageFilters>(initialFilters);
  const [nfs, setNfs] = useState<string[]>([]);

  const { snackbar } = useDialogAlert();
  const { serviceContainer } = useIocContext();
  const { selectedDistributorsStoraged } = useDistributor();

  const storageService = serviceContainer.get<IStorageService>(
    Types.Storage.IStorageService
  );

  const endpoint: StorageEndpoint = useMemo(() => {
    const { hash } = window.location;
    const slug = hash.split("#")[1];
    return slug === routesConfig.STORAGE().RIVER_DISCHARGES
      ? "unload"
      : "retrieval";
  }, []);

  const fetchList = useCallback(
    async (customFilters?: StorageFilters) => {
      const filtersToUse = customFilters || filters;
      try {
        setLoadingData(true);
        const { scheduleDateFrom, scheduleDateTo, ...rest } = filtersToUse;

        const newData: ScheduleList = {
          status: statusVendor,
          customerCompanyId: selectedDistributorsStoraged.join(","),
          scheduleDateFrom: scheduleDateFrom || undefined,
          scheduleDateTo: scheduleDateTo || undefined,
          ...rest,
        };

        const data = Object.fromEntries(
          Object.entries(newData).filter(
            ([_, value]) =>
              value !== undefined && value !== null && value !== ""
          )
        );

        const response = await storageService.getListUnload(
          endpoint,
          data as ScheduleList
        );
        setList(response);
      } catch (error) {
        snackbar({
          message: `Ocorreu um erro ao carregar a lista - ${
            (error as AppError).message
          }`,
          variant: "error",
        });
      } finally {
        setLoadingData(false);
      }
    },
    [
      statusVendor,
      selectedDistributorsStoraged,
      endpoint,
      storageService,
      snackbar,
      filters,
    ]
  );

  const fetchNfsByContract = useCallback(
    async (contractId: string) => {
      try {
        const response = await storageService.getNfsByContract(contractId);
        setNfs(response);
        return response;
      } catch (error) {
        snackbar({
          message: `Não foi possível buscar as notas: ${
            (error as AppError).message
          }`,
          variant: "error",
        });
        setNfs([]);
      }
    },
    [storageService, snackbar]
  );

  const downloadReport = useCallback(async () => {
    try {
      setLoadingData(true);
      const blob = await storageService.downloadReport(endpoint);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      const fileName =
        endpoint === "unload" ? "relatorio_descargas" : "relatorio_entregas";
      link.download = `${fileName}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      snackbar({
        message: `Erro ao baixar relatório: ${(error as AppError).message}`,
        variant: "error",
      });
    } finally {
      setLoadingData(false);
    }
  }, [endpoint, storageService, snackbar]);

  const fetchWithClearFilters = useCallback(() => {
    setFilters(initialFilters);
    fetchList(initialFilters);
  }, [fetchList]);

  const getStorageCompanies = useCallback(async (): Promise<
    ICustomerCompany[]
  > => {
    try {
      const response = await storageService.getListAllStorageCompanies();
      return response;
    } catch (error) {
      snackbar({
        message: `Erro ao carregar empresas de armazenagem: ${
          (error as AppError).message
        }`,
        variant: "error",
      });
      return [];
    }
  }, [storageService, snackbar]);

  const getStorageProducts = useCallback(async (): Promise<IProduct[]> => {
    try {
      const response = await storageService.getListAllStorageProducts();
      return response;
    } catch (error) {
      snackbar({
        message: `Erro ao carregar produtos de armazenagem: ${
          (error as AppError).message
        }`,
        variant: "error",
      });
      return [];
    }
  }, [storageService, snackbar]);

  const getModalities = useCallback(async (): Promise<IModality[]> => {
    try {
      const response = await storageService.getListAllModality();
      return response;
    } catch (error) {
      snackbar({
        message: `Erro ao carregar lista de modalidades. Tentar novamente - ${
          (error as AppError).message
        }`,
        variant: "error",
      });
      return [];
    }
  }, [storageService, snackbar]);

  return (
    <StorageContext.Provider
      value={{
        list,
        tab,
        loadingData,
        statusVendor,
        nfs,
        setStatusVendor,
        setLoadingData,
        setTab,
        setList,
        filters,
        setFilters,
        fetchList,
        fetchWithClearFilters,
        fetchNfsByContract,
        downloadReport,
        getStorageCompanies,
        getStorageProducts,
        getModalities,
      }}
    >
      {children}
    </StorageContext.Provider>
  );
};

export const useStorageContext = (): StorageContextProps => {
  const context = useContext(StorageContext);

  if (!context) {
    throw new Error(
      "useStorageContext deve estar dentro de um StorageProvider"
    );
  }

  return context;
};
